import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { maintenanceApi } from '@/api/maintenance.api';
import { getAllMachines } from '@/api/machine.api';
import { getSuppliers } from '@/api/party.api';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

export default function MaintenanceAMCNewPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const renewContract = location.state?.renewContract;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Master Data State
  const [machines, setMachines] = useState<Array<{ id: string; name: string }>>([]);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string; phone?: string; email?: string }>>([]);

  // Form State
  const [vendorId, setVendorId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [vendorPhone, setVendorPhone] = useState('');
  const [vendorEmail, setVendorEmail] = useState('');

  const [contractType, setContractType] = useState<'COMPREHENSIVE' | 'PARTS_ONLY' | 'LABOUR_ONLY'>('COMPREHENSIVE');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(() => {
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    return nextYear.toISOString().slice(0, 10);
  });
  const [annualCost, setAnnualCost] = useState<number>(50000);
  const [visitFrequency, setVisitFrequency] = useState<string>('Quarterly');
  const [nextVisitDate, setNextVisitDate] = useState<string>('');

  // Selected Machine IDs & Names
  const [selectedMachineIds, setSelectedMachineIds] = useState<string[]>([]);
  const [selectedMachineToAdd, setSelectedMachineToAdd] = useState<string>('');

  const handleAddMachine = () => {
    if (selectedMachineToAdd && !selectedMachineIds.includes(selectedMachineToAdd)) {
      setSelectedMachineIds([...selectedMachineIds, selectedMachineToAdd]);
      setSelectedMachineToAdd('');
    }
  };

  const removeMachine = (mId: string) => {
    setSelectedMachineIds(selectedMachineIds.filter((id) => id !== mId));
  };

  useEffect(() => {
    async function loadMasterOptions() {
      try {
        const [macRes, supRes] = await Promise.all([
          getAllMachines().catch(() => ({ data: [] })),
          getSuppliers().catch(() => ({ data: [] }))
        ]);

        if (macRes?.data && Array.isArray(macRes.data)) {
          setMachines(macRes.data.map((m: any) => ({ id: m.id, name: m.name })));
        } else {
          setMachines([]);
        }

        if (supRes?.data && Array.isArray(supRes.data)) {
          setVendors(supRes.data.map((v: any) => ({
            id: v.id,
            name: v.name,
            phone: v.phone || v.mobile || '',
            email: v.email || ''
          })));
        } else {
          setVendors([]);
        }
      } catch (err) {
        console.error('Failed to load master options:', err);
      }
    }
    loadMasterOptions();
  }, []);

  // Pre-fill form fields when renewing an existing contract
  useEffect(() => {
    if (renewContract) {
      if (renewContract.vendorId) setVendorId(renewContract.vendorId);
      if (renewContract.vendorName) setVendorName(renewContract.vendorName);
      if (renewContract.vendorPhone) setVendorPhone(renewContract.vendorPhone);
      if (renewContract.vendorEmail) setVendorEmail(renewContract.vendorEmail);
      if (renewContract.contractType) setContractType(renewContract.contractType);

      const newStart = renewContract.endDate || new Date().toISOString().slice(0, 10);
      setStartDate(newStart);

      const d = new Date(newStart);
      d.setFullYear(d.getFullYear() + 1);
      setEndDate(d.toISOString().slice(0, 10));

      setAnnualCost(renewContract.annualCost || 0);
      if (renewContract.visitFrequency) setVisitFrequency(renewContract.visitFrequency);
      if (renewContract.machineIds && Array.isArray(renewContract.machineIds)) {
        setSelectedMachineIds(renewContract.machineIds);
      }
    }
  }, [renewContract]);

  const handleVendorSelect = (vId: string) => {
    setVendorId(vId);
    const found = vendors.find((v) => v.id === vId);
    if (found) {
      setVendorName(found.name);
      setVendorPhone(found.phone || '');
      setVendorEmail(found.email || '');
    }
  };

  const vendorOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select Vendor / Enter Manually --' },
    ...vendors.map((v) => ({
      value: v.id,
      label: v.name,
      sublabel: [v.phone, v.email].filter(Boolean).join(' | ')
    }))
  ], [vendors]);

  const contractTypeOptions: SearchableOption[] = useMemo(() => [
    { value: 'COMPREHENSIVE', label: 'COMPREHENSIVE (Parts + Labour)' },
    { value: 'PARTS_ONLY', label: 'PARTS ONLY (Parts Covered)' },
    { value: 'LABOUR_ONLY', label: 'LABOUR ONLY (Service Only)' }
  ], []);

  const visitFrequencyOptions: SearchableOption[] = useMemo(() => [
    { value: 'Monthly', label: 'Monthly' },
    { value: 'Quarterly', label: 'Quarterly' },
    { value: 'Bi-Annual', label: 'Bi-Annual' },
    { value: 'Annual', label: 'Annual' },
    { value: 'On-Call', label: 'On-Call / Emergency Only' }
  ], []);

  const availableMachineOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select Machine to Add to AMC Contract --' },
    ...machines
      .filter((m) => !selectedMachineIds.includes(m.id))
      .map((m) => ({
        value: m.id,
        label: `${m.name} (${m.id})`
      }))
  ], [machines, selectedMachineIds]);

  const toggleMachine = (mId: string) => {
    if (selectedMachineIds.includes(mId)) {
      setSelectedMachineIds(selectedMachineIds.filter((id) => id !== mId));
    } else {
      setSelectedMachineIds([...selectedMachineIds, mId]);
    }
  };

  const handleSave = async () => {
    if (!vendorName.trim()) {
      setError('Vendor / Service Provider name is required');
      return;
    }
    if (!startDate || !endDate) {
      setError('Start date and End date are required');
      return;
    }
    if (selectedMachineIds.length === 0) {
      setError('Please select at least one covered machine');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const machineNames = selectedMachineIds.map(
        (id) => machines.find((m) => m.id === id)?.name || id
      );

      const payload = {
        vendor_id: vendorId || null,
        vendor_name: vendorName.trim(),
        vendor_phone: vendorPhone.trim(),
        vendor_email: vendorEmail.trim(),
        machine_ids: selectedMachineIds,
        machine_names: machineNames,
        contract_type: contractType,
        start_date: startDate,
        end_date: endDate,
        annual_cost: Number(annualCost) || 0,
        visit_frequency: visitFrequency,
        next_visit_date: nextVisitDate || null
      };

      await maintenanceApi.createAMCContract(payload);
      toast.success(renewContract ? 'AMC Contract renewed successfully!' : 'AMC Contract registered successfully!');
      navigate('/manufacturing/maintenance?tab=amc');
    } catch (err: any) {
      console.error('Error creating AMC contract:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to create AMC contract');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full w-full">
        {/* Header: Back Button + Title */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/manufacturing/maintenance?tab=amc')}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 cursor-pointer shadow-xs transition-colors"
            title="Back to AMC Contracts"
          >
            <i className="ri-arrow-left-line text-lg" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-[#1e293b]">
              {renewContract ? `Renew AMC Contract (${renewContract.contractNumber})` : 'Create AMC Contract'}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {renewContract
                ? `Renewing agreement for ${renewContract.vendorName}. Review & update new annual cost, terms, or covered machines.`
                : 'Register vendor Annual Maintenance Contract & assigned machines'}
            </p>
          </div>
        </div>

        {/* Error Notification */}
        {error && (
          <div className="mb-5 px-4 py-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium flex items-center gap-2.5 shadow-xs">
            <i className="ri-error-warning-line text-xl text-rose-600" />
            <span>{error}</span>
          </div>
        )}

        {/* ONE SINGLE FULL-WIDTH CARD */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-sm space-y-8">
          
          {/* SECTION 1: Vendor Information */}
          <div>
            <h2 className="text-sm font-bold text-[#1e293b] uppercase tracking-wider mb-4 pb-2 border-b border-slate-100 flex items-center gap-2">
              <i className="ri-building-4-line text-blue-600 text-base" />
              Vendor & Service Provider Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Vendor Name <span className="text-rose-500">*</span>
                </label>
                {vendors.length > 0 ? (
                  <div className="space-y-2">
                    <SearchableSelect
                      value={vendorId}
                      onChange={(val) => handleVendorSelect(val)}
                      options={vendorOptions}
                      placeholder="-- Select Vendor / Enter Manually --"
                      searchPlaceholder="Search vendor..."
                    />
                    {!vendorId && (
                      <input
                        type="text"
                        placeholder="Or enter vendor name..."
                        value={vendorName}
                        onChange={(e) => setVendorName(e.target.value)}
                        className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                      />
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder="e.g. PressServ Solutions Pvt Ltd"
                    value={vendorName}
                    onChange={(e) => setVendorName(e.target.value)}
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                  />
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Vendor Phone Number
                </label>
                <input
                  type="text"
                  placeholder="e.g. +91 98765 43210"
                  value={vendorPhone}
                  onChange={(e) => setVendorPhone(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Vendor Support Email
                </label>
                <input
                  type="email"
                  placeholder="e.g. service@vendor.com"
                  value={vendorEmail}
                  onChange={(e) => setVendorEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                />
              </div>
            </div>
          </div>

          {/* SECTION 2: Contract Terms & Scope */}
          <div>
            <h2 className="text-sm font-bold text-[#1e293b] uppercase tracking-wider mb-4 pb-2 border-b border-slate-100 flex items-center gap-2">
              <i className="ri-[#1e293b] ri-file-paper-2-line text-indigo-600 text-base" />
              Contract Terms & Financials
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Contract Type <span className="text-rose-500">*</span>
                </label>
                <SearchableSelect
                  value={contractType}
                  onChange={(val) => setContractType(val as any)}
                  options={contractTypeOptions}
                  placeholder="Select Contract Type"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Start Date <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  End Date <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Annual Contract Cost (₹)
                </label>
                <input
                  type="number"
                  min={0}
                  placeholder="e.g. 120000"
                  value={annualCost}
                  onChange={(e) => setAnnualCost(parseFloat(e.target.value) || 0)}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Visit Frequency
                </label>
                <SearchableSelect
                  value={visitFrequency}
                  onChange={(val) => setVisitFrequency(val)}
                  options={visitFrequencyOptions}
                  placeholder="Select Frequency"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Next Scheduled Service Visit
                </label>
                <input
                  type="date"
                  value={nextVisitDate}
                  onChange={(e) => setNextVisitDate(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                />
              </div>
            </div>
          </div>

          {/* SECTION 3: Covered Machines Selection */}
          <div>
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-100">
              <h2 className="text-sm font-bold text-[#1e293b] uppercase tracking-wider flex items-center gap-2">
                <i className="ri-cpu-line text-emerald-600 text-base" />
                Covered Machinery & Equipment <span className="text-rose-500">*</span>
              </h2>
              <span className="text-xs font-semibold text-[#2563eb] bg-blue-50 border border-blue-200 px-3 py-1 rounded-full">
                {selectedMachineIds.length} Machine(s) Covered
              </span>
            </div>

            {/* Dropdown to Select Machine */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1">
                <SearchableSelect
                  value={selectedMachineToAdd}
                  onChange={(val) => setSelectedMachineToAdd(val)}
                  options={availableMachineOptions}
                  placeholder="-- Select Machine to Add to AMC Contract --"
                  searchPlaceholder="Search machine..."
                />
              </div>

              <button
                type="button"
                onClick={handleAddMachine}
                disabled={!selectedMachineToAdd}
                className="px-4 py-2.5 rounded-lg bg-[#2563eb] hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-semibold cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 shadow-sm transition-colors"
              >
                <i className="ri-add-line text-sm" /> Add Machine
              </button>
            </div>

            {/* Added Machines List Table */}
            {selectedMachineIds.length > 0 ? (
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="text-left px-4 py-2.5">#</th>
                      <th className="text-left px-4 py-2.5">Machine Name</th>
                      <th className="text-left px-4 py-2.5">Machine Code / ID</th>
                      <th className="text-right px-4 py-2.5">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedMachineIds.map((mId, idx) => {
                      const mac = machines.find((m) => m.id === mId);
                      return (
                        <tr key={mId} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                          <td className="px-4 py-2.5 text-xs text-slate-400 font-semibold">{idx + 1}</td>
                          <td className="px-4 py-2.5 text-sm font-bold text-[#1e293b]">{mac ? mac.name : mId}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{mId}</td>
                          <td className="px-4 py-2.5 text-right">
                            <button
                              type="button"
                              onClick={() => removeMachine(mId)}
                              className="px-2.5 py-1 rounded-lg text-rose-600 hover:bg-rose-50 border border-rose-200 text-xs font-semibold inline-flex items-center gap-1 cursor-pointer transition-colors"
                            >
                              <i className="ri-delete-bin-line text-xs" /> Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                <i className="ri-cpu-line text-3xl text-slate-400 mb-1 block" />
                <p className="text-xs text-slate-500 font-medium">No machines added yet.</p>
                <p className="text-[11px] text-slate-400">Select a machine from the dropdown above and click "Add Machine".</p>
              </div>
            )}
          </div>

          {/* Actions Footer */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={() => navigate('/manufacturing/maintenance')}
              className="px-4 py-2 text-sm font-semibold text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-100 bg-white cursor-pointer transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              onClick={handleSave}
              className="px-5 py-2 text-sm font-semibold text-white bg-[#2563eb] hover:bg-blue-700 rounded-lg cursor-pointer disabled:opacity-50 inline-flex items-center gap-2 shadow-sm transition-colors"
            >
              {saving ? (
                <i className="ri-loader-4-line animate-spin text-base" />
              ) : (
                <i className="ri-save-line text-base" />
              )}
              Save AMC Contract
            </button>
          </div>

        </div>
      </div>
    </AppLayout>
  );
}
